// Adaptive deposit widget — ONE surface that shows exactly the next step:
//   connect → (no USDC on HyperEVM) bring funds over → deposit.
//
// 🔴 We do NOT run the cross-chain bridge ourselves. Bridging is the hardest, most
// failure-prone UX in crypto — gas estimation, multi-signature flows, stuck txs,
// popups on the wrong screen — and a hand-rolled version produced exactly those
// failures on the first real user. So we hand bridging to Jumper (LI.FI's own
// hardened consumer app), pre-filled to deliver our vault's USDC to HyperEVM and
// opened in its own tab with full wallet support. The deposit itself stays a simple
// approve + deposit. Same component on /deposit and the per-vault page.

import { VAULT_CONFIGS, VAULT_ABI, ERC20_ABI, USDC_ADDRESS, USDC_DECIMALS } from './config.js';
import { getAddress, getSigner, getReadProvider } from './wallet.js';
import { VAULT_USDC, HYPEREVM_CHAIN_ID } from './zap-core.mjs';
import { showTxModal, txModalSuccess, txModalError } from './tx.js';

const GAS_MIN_HYPE = 0.005;          // below this, the deposit tx may not have gas
const CHAIN_HEX = (id) => '0x' + id.toString(16);
// Jumper (LI.FI's consumer app) pre-filled to deliver our vault's USDC to HyperEVM.
const JUMPER_URL = `https://jumper.exchange/?toChain=${HYPEREVM_CHAIN_ID}&toToken=${VAULT_USDC}`;

const _esc = (s) => String(s);
const _icon = (name, cls = '', px = 18) => `<span class="material-symbols-outlined ${cls}" style="font-size:${px}px;line-height:1;">${name}</span>`;

async function _hyperUsdc(addr) {
  try { return Number(await new ethers.Contract(VAULT_USDC, ERC20_ABI, getReadProvider()).balanceOf(addr)) / 1e6; } catch { return 0; }
}
async function _hyperHype(addr) {
  try { return Number(await getReadProvider().getBalance(addr)) / 1e18; } catch { return 0; }
}

// Ensure the wallet is on HyperEVM (add it if MetaMask doesn't know it) before a
// deposit tx. Returns false only if the user rejected the switch.
async function _ensureHyperEvm() {
  try {
    if (parseInt(await window.ethereum.request({ method: 'eth_chainId' }), 16) === HYPEREVM_CHAIN_ID) return true;
    await window.ethereum.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: CHAIN_HEX(HYPEREVM_CHAIN_ID) }] });
    return true;
  } catch (e) {
    if (e?.code === 4001) return false;
    if (e?.code === 4902) {
      try {
        await window.ethereum.request({ method: 'wallet_addEthereumChain', params: [{
          chainId: CHAIN_HEX(HYPEREVM_CHAIN_ID), chainName: 'HyperEVM Mainnet',
          nativeCurrency: { name: 'HYPE', symbol: 'HYPE', decimals: 18 },
          rpcUrls: ['https://rpc.hyperliquid.xyz/evm'], blockExplorerUrls: ['https://hyperevmscan.io'],
        }] });
        return true;
      } catch (e2) { return e2?.code !== 4001; }
    }
    return false;
  }
}

export function mountDepositWidget(container, vaultKey) {
  const k = String(vaultKey).toLowerCase();
  const vc = VAULT_CONFIGS.find(v => v.key === vaultKey || v.address?.toLowerCase() === k || v.name?.toLowerCase() === k);
  if (!container || !vc || !vc.address) return;
  const state = { vc, minDeposit: 1, sharePrice: 1, step: null };
  const $ = (sel) => container.querySelector(sel);
  const render = (html) => { container.innerHTML = html; };

  function stepper(active) {
    const base = 'w-5 h-5 rounded-full inline-flex items-center justify-center text-[11px] font-headline font-bold shrink-0';
    const dot = (n, on, done) =>
      done ? `<span class="${base} bg-tertiary text-on-primary">${_icon('check', '', 13)}</span>`
      : on ? `<span class="${base} bg-primary text-on-primary">${n}</span>`
      : `<span class="${base} border border-outline-variant/50 text-on-surface-variant">${n}</span>`;
    const lbl = (on) => on ? 'text-on-surface' : 'text-on-surface-variant';
    return `<div class="flex items-center gap-2 text-xs mb-4">
      ${dot(1, active === 'fund', active === 'deposit')}<span class="font-label ${lbl(active === 'fund')}">Add USDC</span>
      <span class="flex-1 h-px bg-outline-variant/40"></span>
      ${dot(2, active === 'deposit', false)}<span class="font-label ${lbl(active === 'deposit')}">Deposit</span>
    </div>`;
  }

  const primaryBtn = 'w-full py-3 bg-gradient-to-br from-primary to-primary-container text-on-primary font-headline font-bold rounded-DEFAULT hover:shadow-[0_0_24px_rgba(138,235,255,0.4)] transition-all duration-300 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none';

  function renderConnect() {
    state.step = 'connect';
    render(`${stepper(null)}
      <div class="text-center py-5 text-sm text-on-surface-variant">Connect your wallet to deposit into ${_esc(vc.name)}.</div>
      <button id="dw-connect" class="${primaryBtn}">Connect wallet</button>`);
    $('#dw-connect')?.addEventListener('click', () => document.getElementById('connect-btn')?.click());
  }

  function renderFund() {
    state.step = 'fund';
    const addr = getAddress();
    const short = addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : '';
    render(`${stepper('fund')}
      <div class="text-xs text-on-surface-variant mb-3">To deposit you need USDC on HyperEVM. Bring it over from any chain in one step:</div>
      <a href="${JUMPER_URL}" target="_blank" rel="noopener" class="${primaryBtn} flex items-center justify-center gap-2 mb-2">
        ${_icon('swap_horiz', '', 20)} Bridge USDC to HyperEVM
      </a>
      <div class="text-xs text-on-surface-variant bg-surface-container-high rounded-lg border border-outline-variant/20 p-3 mb-3 flex items-start gap-2">
        ${_icon('info', 'text-secondary-fixed-dim shrink-0')}
        <span>Opens Jumper — pick any chain you hold USDC on and it delivers it here. Turn on <span class="text-on-surface">"Get gas"</span> there so you also receive a little HYPE for fees.</span>
      </div>
      ${addr ? `<div class="text-[0.7rem] text-on-surface-variant mb-3">Or send USDC straight to your address on HyperEVM:
        <button id="dw-copy" class="inline-flex items-center gap-1 text-primary hover:underline ml-1 align-middle">${short} ${_icon('content_copy', '', 13)}</button></div>` : ''}
      <button id="dw-recheck" class="w-full py-2.5 bg-surface-container-high text-on-surface font-headline font-semibold rounded-DEFAULT border border-outline-variant/30 hover:border-primary/40 transition-colors flex items-center justify-center gap-2">
        ${_icon('refresh', '', 18)} I've added USDC — check again
      </button>`);
    $('#dw-copy')?.addEventListener('click', async () => {
      try { await navigator.clipboard.writeText(addr); const b = $('#dw-copy'); if (b) { b.textContent = 'Copied ✓'; setTimeout(renderFund, 1200); } } catch {}
    });
    $('#dw-recheck')?.addEventListener('click', async () => {
      const b = $('#dw-recheck');
      if (b) { b.disabled = true; b.innerHTML = `${_icon('progress_activity', 'animate-spin', 18)} Checking…`; }
      await refresh();   // re-reads HyperEVM USDC; flips to Deposit if it arrived
      // Still on the fund step → funds haven't landed yet; say so on the button.
      if (state.step === 'fund') { const nb = $('#dw-recheck'); if (nb) nb.innerHTML = `${_icon('refresh', '', 18)} No USDC found yet — check again`; }
    });
  }

  function renderDeposit(usdc, lowGas) {
    state.step = 'deposit';
    render(`${stepper('deposit')}
      <div class="text-xs text-on-surface-variant mb-3">Deposit into ${_esc(vc.name)}.</div>
      <div class="flex items-center bg-surface-container-high rounded-lg border border-outline-variant/20 overflow-hidden mb-2">
        <input id="dw-amt" type="number" placeholder="0.00" class="flex-1 bg-transparent px-4 py-3 text-lg font-headline text-on-surface outline-none" />
        <button id="dw-max" class="px-3 py-1 text-xs text-primary border border-primary/30 rounded hover:bg-primary/10 transition-colors">MAX</button>
        <span class="px-4 text-sm text-secondary">USDC</span>
      </div>
      <div class="flex justify-between text-xs text-on-surface-variant mb-3">
        <span id="dw-recv">You receive: <span class="text-on-surface font-semibold">0.0000</span> ${_esc(vc.symbol || 'shares')}</span>
        <span>Balance: <span class="text-primary">${usdc.toFixed(2)}</span></span>
      </div>
      ${lowGas ? `<div class="text-xs text-on-surface-variant bg-surface-container-high rounded-lg border border-secondary-fixed-dim/30 p-3 mb-3 flex items-start gap-2">
        ${_icon('local_gas_station', 'text-secondary-fixed-dim shrink-0')}
        <span>You're low on HYPE for gas. Bridge a little more with <a href="${JUMPER_URL}" target="_blank" rel="noopener" class="text-primary underline">Jumper</a> ("Get gas" on) so the deposit can go through.</span>
      </div>` : ''}
      <label class="flex items-center gap-2 text-xs text-on-surface-variant mb-3 cursor-pointer">
        <input id="dw-risk" type="checkbox" class="w-3.5 h-3.5 accent-primary" />
        <span>I understand I may lose some or all of my deposit · <a href="terms.html" target="_blank" class="text-primary hover:underline">terms</a></span>
      </label>
      <div id="dw-err" class="hidden text-xs text-error mb-3 flex items-center gap-1">${_icon('warning', '', 16)}<span id="dw-err-msg"></span></div>
      <button id="dw-deposit" class="${primaryBtn}">Deposit into ${_esc(vc.name)}</button>
      <div class="text-[0.7rem] text-on-surface-variant mt-3">20% fee on profits only · withdraw anytime (1-hour lock)</div>`);
    const amt = $('#dw-amt');
    const updateRecv = () => { const a = parseFloat(amt.value) || 0; $('#dw-recv').innerHTML = `You receive: <span class="text-on-surface font-semibold">~${(a / (state.sharePrice || 1)).toFixed(4)}</span> ${vc.symbol || 'shares'}`; };
    amt?.addEventListener('input', updateRecv);
    $('#dw-max')?.addEventListener('click', () => { amt.value = usdc.toFixed(2); updateRecv(); });
    $('#dw-deposit')?.addEventListener('click', () => _doDeposit(usdc));
  }

  async function _doDeposit(usdcBal) {
    const amt = parseFloat($('#dw-amt')?.value);
    const fail = (m) => { const ms = $('#dw-err-msg'); if (ms) ms.textContent = m; $('#dw-err')?.classList.remove('hidden'); };
    if (!amt || amt <= 0) return fail('Enter an amount.');
    if (amt < state.minDeposit) return fail(`Minimum is $${state.minDeposit} USDC.`);
    if (amt > usdcBal + 1e-9) return fail('More than your balance.');
    if (!$('#dw-risk')?.checked) return fail('Please confirm you understand the risk.');
    const signer = getSigner();
    if (!signer) return fail('Connect your wallet.');
    if (!(await _ensureHyperEvm())) return fail('Switch to HyperEVM Mainnet to deposit.');
    const addr = getAddress();
    const vault = new ethers.Contract(vc.address, VAULT_ABI, signer);
    const usdc = new ethers.Contract(USDC_ADDRESS, ERC20_ABI, signer);
    const assets = BigInt(Math.floor(amt * 10 ** USDC_DECIMALS));
    try {
      if ((await usdc.allowance(addr, vc.address)) < assets) {
        showTxModal('Approving USDC', 'Please confirm in MetaMask…');
        await (await usdc.approve(vc.address, ethers.MaxUint256)).wait();
      }
      showTxModal('Depositing ' + amt.toFixed(2) + ' USDC', 'Please confirm in MetaMask…');
      const receipt = await (await vault.deposit(assets, addr, { gasLimit: 500000 })).wait();
      txModalSuccess('Deposited ' + amt.toFixed(2) + ' USDC into ' + vc.name, receipt.hash);
      refresh();
    } catch (e) {
      if (e?.code === 4001 || e?.code === 'ACTION_REJECTED') { txModalError && txModalError('Cancelled — no funds moved.'); }
      else txModalError && txModalError(e?.shortMessage || e?.message || 'Deposit failed.');
    }
  }

  async function refresh() {
    const addr = getAddress();
    if (!addr) return renderConnect();
    try {
      const v = new ethers.Contract(vc.address, VAULT_ABI, getReadProvider());
      const [min, ta, ts] = await Promise.all([v.minDeposit().catch(() => 1000000n), v.totalAssets().catch(() => 0n), v.totalSupply().catch(() => 0n)]);
      state.minDeposit = Math.max(1, Math.round(Number(min) / 1e6));
      // RAW price/share (what the contract mints against), NOT the launch-normalized
      // display price — used only for the "you receive" estimate.
      state.sharePrice = ts > 0n ? (Number(ta) / 1e6) / (Number(ts) / 1e9) : 1;
    } catch {}
    const [usdc, hype] = await Promise.all([_hyperUsdc(addr), _hyperHype(addr)]);
    if (usdc >= state.minDeposit) return renderDeposit(usdc, hype < GAS_MIN_HYPE);
    renderFund();
  }

  // Re-render on wallet change, and when the user returns to this tab on the fund
  // step (e.g. back from bridging on Jumper) — without stacking listeners across
  // re-mounts, and without wiping a half-typed deposit amount on tab-focus.
  const onFocus = () => { if (state.step === 'fund') refresh(); };
  if (container._dwClean) container._dwClean();
  window.addEventListener('wallet-changed', refresh);
  window.addEventListener('focus', onFocus);
  container._dwClean = () => { window.removeEventListener('wallet-changed', refresh); window.removeEventListener('focus', onFocus); };
  refresh();
}
